/**
 * Authentication Service
 *
 * Microservice for managing user authentication with Google OAuth
 * Handles sign in, sign up, sign out, and session management
 */

import { supabase } from './supabaseClient';

/**
 * Sign in with Google OAuth
 * Redirects to onboarding (which auto-redirects to dashboard if already completed)
 * Requests Gmail read permissions to auto-add Gmail account
 * @returns {Promise<Object>} Auth response
 */
export const signInWithGoogle = async () => {
  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/login`,
        scopes: 'email profile https://www.googleapis.com/auth/gmail.readonly',
        queryParams: {
          access_type: 'offline',
          prompt: 'select_account',  // Don't force consent for returning users
        }
      }
    });

    if (error) {
      console.error('Error signing in with Google:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('signInWithGoogle error:', error);
    throw error;
  }
};

/**
 * Sign up with Google OAuth
 * Redirects to onboarding after successful signup
 * Requests Gmail read permissions to auto-add Gmail account
 * @returns {Promise<Object>} Auth response
 */
export const signUpWithGoogle = async () => {
  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/signup`,
        scopes: 'email profile https://www.googleapis.com/auth/gmail.readonly',
        queryParams: {
          access_type: 'offline',
          prompt: 'consent'
        }
      }
    });

    if (error) {
      console.error('Error signing up with Google:', error);
      throw error;
    }

    console.log('✅ Signup initiated - will redirect to onboarding');
    return data;
  } catch (error) {
    console.error('signUpWithGoogle error:', error);
    throw error;
  }
};

/**
 * Re-authenticate with Google to get Gmail consent
 * Used during onboarding when a sign-in user needs Gmail permissions
 * Redirects through Supabase's Google OAuth with prompt: consent
 * @returns {Promise<Object>} Auth response
 */
export const reauthWithGmailConsent = async () => {
  try {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/signup`,
        scopes: 'email profile https://www.googleapis.com/auth/gmail.readonly',
        queryParams: {
          access_type: 'offline',
          prompt: 'consent'
        }
      }
    });

    if (error) {
      throw error;
    }

    return data;
  } catch (error) {
    console.error('reauthWithGmailConsent error:', error);
    throw error;
  }
};

/**
 * Sign out the current user
 * @returns {Promise<void>}
 */
export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut({ scope: 'global' });

    if (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  } catch (error) {
    console.error('signOut error:', error);
    throw error;
  }
};

/**
 * Get the current authenticated user
 * @returns {Promise<Object|null>} User object or null
 */
export const getCurrentUser = async () => {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();

    if (error) {
      console.error('Error getting current user:', error);
      throw error;
    }

    return user;
  } catch (error) {
    console.error('getCurrentUser error:', error);
    return null;
  }
};

/**
 * Get the current session
 * @returns {Promise<Object|null>} Session object or null
 */
export const getSession = async () => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();

    if (error) {
      console.error('Error getting session:', error);
      throw error;
    }

    return session;
  } catch (error) {
    console.error('getSession error:', error);
    return null;
  }
};

/**
 * Subscribe to auth state changes
 * @param {Function} callback - Callback function to handle auth changes
 * @returns {Object} Subscription object with unsubscribe method
 */
export const onAuthStateChange = (callback) => {
  const { data: { subscription } } = supabase.auth.onAuthStateChange(
    (event, session) => {
      callback(event, session);
    }
  );

  return subscription;
};

/**
 * Check if user is authenticated
 * @returns {Promise<boolean>} True if authenticated, false otherwise
 */
export const isAuthenticated = async () => {
  const session = await getSession();
  return !!session;
};

/**
 * Signup location detection (pre-fetched on load, non-blocking)
 * Same approach as waitlistService.js
 */
let cachedLocation = null;
const locationPromise = (async () => {
  try {
    const res = await fetch('https://get.geojs.io/v1/ip/geo.json', { signal: AbortSignal.timeout(3000) });
    if (!res.ok) return;
    const data = await res.json();
    cachedLocation = {
      country: data.country || null,
      city: data.city || null,
      timezone: data.timezone || null,
    };
  } catch {
    // silently fail — location is optional
  }
})();

/**
 * Sync user data to database after OAuth login
 * Creates or updates user record with Google account info
 * On first signup, also captures country/city/timezone from IP
 * @param {Object} user - User object from Supabase auth
 * @returns {Promise<Object>} Created/updated user record
 */
export const syncUserToDatabase = async (user) => {
  try {
    if (!user) {
      throw new Error('No user provided to sync');
    }

    // Check if user already exists (to avoid overwriting location on subsequent logins)
    const { data: existing } = await supabase
      .from('users')
      .select('id, signup_country')
      .eq('id', user.id)
      .single();

    const upsertData = {
      id: user.id,
      email: user.email,
      full_name: user.user_metadata?.full_name || user.user_metadata?.name || null,
      avatar_url: user.user_metadata?.avatar_url || user.user_metadata?.picture || null,
      updated_at: new Date().toISOString()
    };

    // Only set location on first signup (when user doesn't exist or has no country yet)
    if (!existing || !existing.signup_country) {
      const loc = cachedLocation;
      if (loc) {
        upsertData.signup_country = loc.country;
        upsertData.signup_city = loc.city;
        upsertData.signup_timezone = loc.timezone;
      }
    }

    const { data, error } = await supabase
      .from('users')
      .upsert(upsertData, {
        onConflict: 'id'
      })
      .select()
      .single();

    if (error) {
      console.error('Error syncing user to database:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('syncUserToDatabase error:', error);
    throw error;
  }
};

export default {
  signInWithGoogle,
  signUpWithGoogle,
  signOut,
  getCurrentUser,
  getSession,
  onAuthStateChange,
  isAuthenticated,
  syncUserToDatabase
};
